import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

function AviatorPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  const [loading, setLoading]   = useState(true);
  const [lobbyURL, setLobbyURL] = useState(null);
  const [error, setError]       = useState("");

  const launch = async () => {
    if (!user?.id) { navigate("/login"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await API.post("/api/game/launch", { user_id: user.id, tpGameId: 1030, tableCode: "Aviator" });
      const { lobbyURL: url, useURL } = res.data || {};
      if (useURL && url) {
        window.open(url, "_blank", "noopener,noreferrer");
        navigate("/dashboard");
      } else if (url) {
        setLobbyURL(url);
      } else {
        setError("Could not load game. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to launch game. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { launch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll + pull-to-refresh when iframe is open
  useEffect(() => {
    if (!lobbyURL) return;
    const scrollY = window.scrollY;
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.documentElement.style.overflow = "";
      document.documentElement.style.overscrollBehavior = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    };
  }, [lobbyURL]);

  // ── Full-screen game (no top bar) ────────────────────────────────────────────
  if (lobbyURL) {
    return (
      <div style={wrap}>
        <iframe
          src={lobbyURL}
          title="Aviator Game"
          style={frame}
          allow="autoplay; fullscreen; payment; clipboard-write; microphone; camera"
          allowFullScreen
        />
      </div>
    );
  }

  // ── Loading / error screen ───────────────────────────────────────────────────
  return (
    <div style={splash}>
      {/* Background glow blobs */}
      <div style={blob1} />
      <div style={blob2} />

      {loading ? (
        <div style={loadCard}>
          <div style={planeRing}>
            <span style={planeEmoji}>✈</span>
            <svg style={ringsvg} viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(250,204,21,0.12)" strokeWidth="5" />
              <circle cx="50" cy="50" r="44" fill="none" stroke="#facc15" strokeWidth="5"
                strokeDasharray="80 196" strokeLinecap="round"
                style={{ animation: "spin 1.2s linear infinite", transformOrigin: "50% 50%" }} />
            </svg>
          </div>
          <div style={loadTitle}>AVIATOR</div>
          <div style={loadSub}>Launching game, please wait…</div>
          <div style={dotRow}>
            <span style={{ ...dot, animationDelay: "0s" }} />
            <span style={{ ...dot, animationDelay: "0.2s" }} />
            <span style={{ ...dot, animationDelay: "0.4s" }} />
          </div>
        </div>
      ) : (
        <div style={loadCard}>
          <div style={errIcon}>⚠️</div>
          <div style={loadTitle}>Oops!</div>
          <div style={errText}>{error || "Something went wrong."}</div>
          <button type="button" style={retryBtn} onClick={launch}>↺ Try Again</button>
          <button type="button" style={backBtn} onClick={() => navigate("/dashboard")}>← Back to Home</button>
        </div>
      )}
    </div>
  );
}

/* ── styles ── */
const wrap  = { position: "fixed", inset: 0, zIndex: 9999, background: "#000", touchAction: "none", overscrollBehavior: "none", overflow: "hidden" };
const frame = { width: "100%", height: "100%", border: "none", display: "block", touchAction: "auto" };

const splash = { position: "fixed", inset: 0, zIndex: 9999, background: "linear-gradient(160deg,#07071a 0%,#0f0c24 60%,#150a1e 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, system-ui, sans-serif", overflow: "hidden" };

const blob1 = { position: "absolute", top: "-20%", left: "-10%", width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)", pointerEvents: "none" };
const blob2 = { position: "absolute", bottom: "-15%", right: "-10%", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(250,204,21,0.12) 0%, transparent 70%)", pointerEvents: "none" };

const loadCard = { position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "40px 32px", borderRadius: 28, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", boxShadow: "0 24px 64px rgba(0,0,0,0.5)", maxWidth: 320, width: "90%" };

const planeRing = { position: "relative", width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center" };
const ringsvg   = { position: "absolute", inset: 0, width: "100%", height: "100%" };
const planeEmoji= { fontSize: 40, lineHeight: 1, filter: "drop-shadow(0 0 16px rgba(250,204,21,0.7))", position: "relative", zIndex: 1 };

const loadTitle = { fontSize: 26, fontWeight: 900, color: "#facc15", letterSpacing: "0.14em", textShadow: "0 0 24px rgba(250,204,21,0.4)" };
const loadSub   = { fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 600, letterSpacing: "0.02em", textAlign: "center" };

const dotRow = { display: "flex", gap: 8, marginTop: 4 };
const dot    = { width: 7, height: 7, borderRadius: "50%", background: "#facc15", display: "inline-block", animation: "pulse 1.2s ease-in-out infinite", opacity: 0.3 };

const errIcon = { fontSize: 44, lineHeight: 1 };
const errText = { fontSize: 14, color: "rgba(255,255,255,0.55)", fontWeight: 600, textAlign: "center", lineHeight: 1.5, padding: "0 8px" };
const retryBtn= { width: "100%", height: 48, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#ea580c,#ea580c)", color: "#fff", fontSize: 15, fontWeight: 900, cursor: "pointer", letterSpacing: "0.04em", boxShadow: "0 6px 20px rgba(124,58,237,0.4)" };
const backBtn = { width: "100%", height: 40, border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, background: "transparent", color: "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: 700, cursor: "pointer" };

export default AviatorPage;
